'use strict';

/**
 * Webduct Constants Helper
 *
 * @version
 * 3.13.0 LIBENV-279 Add method to get the Submit Order routing constant
 * 3.10.0 LIBENV-272 Add method to get the default value of the "Automatically Select Recommended Joints" constant
 * 3.4.0 LIBENV-258 Add getDefaultMeasuredByValue constant
 * 3.3.0 LIBENV-254 Deployment logos: change to use CloudFront/S3 URL
 * 3.3.0 LIBENV-253 Remove isModernUserDataSource method from WebductConstants
 * 3.1.0 LIBENV-246 Remove getOrderCrudApiRouting()
 * 2.57.0 LIBENV-242 Add method to get the order CRUD Api routing constant
 * 2.56.0 LIBENV-241 Constants: add method to get clone for crews flag from application_vars
 * 2.54.0 LIBENV-233 Changes required for landing page
 * 2.47.0 LIBENV-218 Constants: add method to get Productivity Report flag from application_vars
 * 2.42.0 LIBENV-201 Remove checking of deprecated application vars: billing, order-admin-ui, timecards
 * 2.37.0 LIBENV-191 Refactor to always use calendar-ui-v2 for URLs
 * 2.36.0 LIBENV-188 Changes to allow more control over show/hiding Report links
 * 2.34.1 LIBENV-182 Remove references to old UI routing configs
 * 2.34.0 LIBENV-180 Implement WD_ENABLE_ORDER_ADMIN_V2 constant
 * 2.33.0 LIBENV-178 Implement WD_ENABLE_CALENDAR_V2 constant
 * 2.23.0 LIBENV-142 Billing Report: support new urls
 * 2.4.0 LIBENV-109 Add method to get the routing for PDF Exports
 * 2.2.0 LIBENV-103 Add method to get the default country constant
 * 2.1.0 LIBENV-101 Add method to get the default region constant
 * 1.27.0 LIBENV-88 Add getPublicApiPath and getDefaultCamSystem
 * 1.21.0 LIBENV-76 Add a product report fulfillment group whitelist constant
 * 1.16.0 LIBENV-66 Add isMetric() method to constants
 * 1.15.0 LIBENV-53 Add WebductConstants into globalThis for ES6 usage
 * 1.9.0 LIBENV-51 Don't allow metric hints for non-metric deployments
 * 1.8.0 LIBENV-49 Add constant to allow Imperial hints and Metric conversion
 * 1.6.0 LIBENV-43 Add a new method to get the constant WD_ORDERING_UI_JOB_TYPES_FILTER
 * 1.4.4 LIBENV-35 Refactor WebductConstants
 * 1.4.0 LIBENV-26 Add new 'locale' & deliverable countries' constants to Webduct Constants
 * 1.0.0 LIBENV-1 Initialize environment library
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @author Erik Orozco <eorozco@intersog.com>
 * @author Luis Hernandez <lhernandez@intersog.com>
 * @author Jenny Lam <jlam@buildcentrix.com>
 * @author Deitric Jang <djang@buildcentrix.com>
 *
 * @copyright (c) 2018-2024 Build Centrix
 *
 */
var WebductConstants = function () {

    // Controller
    var helper = {};

    // Constants
    var constants = {};

    /************************************** External Functionality ****************************************************/

    /**
     * Get the default value for the "Automatically Select Recommended Joints" checkbox
     *
     * @since 3.10.0
     * @returns {String}
     */
    helper.getAutoSelectJointsDefaultValue = function() {
        return getConstant('WD_AUTO_SELECT_JOINTS_DEFAULT');
    };

    /**
     * Get Build Centrix Company Name
     * @since 2.54.0
     * @returns {String}
     */
    helper.getBcxCompanyName = function() {
        return 'BuildCentrix'
    };

    /**
     * Get CDN (CloudFront) Base URL
     *
     * @since 3.3.0
     * @returns {String}
     */
    helper.getCdnBaseUrl = function() {
        return getConstant('WD_CDN_BASE_URL');
    };

    /**
     * Get Coil Length
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getCoilLength = function () {
        return getConstant('WD_COIL_LENGTH');
    };

    /**
     * Get Company Name
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getCompanyName = function () {
        return getConstant('WD_COMPANY_NAME');
    };

    /**
     * Get Company Minimum Days Required (for Production)
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getCompanyMinimumDaysRequired = function () {
        return getConstant('WD_ORDER_MINIMUM_DAYS_REQUIRED');
    };

    /**
     * Get Company Phone Number
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getCompanyPhoneNumber = function () {
        return getConstant('WD_COMPANY_PHONE_NUMBER');
    };

    /**
     * Get Product Report fulfillment group whitelist
     *
     * @since 1.21.0
     * @returns {String}
     */
    helper.getDashboardUIFulfillmentGroupFilter = function () {
        return getConstant('WD_DASHBOARD_UI_FULFILLMENT_GROUP_FILTER') || 'disabled';
    };

    /**
     * Get Default CAM System
     *
     * @since 1.27.0
     * @returns {String}
     */
    helper.getDefaultCamSystem = function () {
        return getConstant('DEFAULT_CAM_SOFTWARE_VERSION');
    };

    /**
     * Get Default Country
     * This constant is used for defaulting the country when adding reimbursement entries and filtering entries report.
     *
     * @since 2.2.0
     * @returns {String}
     */
    helper.getDefaultCountry = function () {
        return getConstant('WD_DEFAULT_COUNTRY');
    };

    /**
     * Get "Measured By" default value constant
     * This constant is used to determine what the "Measured By" default value is set to on the ordering-ui submit order page.
     *
     * @since 3.4.0
     * @returns {String}
     */
    helper.getDefaultMeasuredByValue = function () {
        return getConstant('WD_MEASURED_BY_DEFAULT');
    };

    /**
     * Get Default Region
     * This constant is used for defaulting the region when adding reimbursement entries.
     *
     * @since 2.1.0
     * @returns {String}
     */
    helper.getDefaultRegion = function () {
        return getConstant('WD_DEFAULT_REGION');
    };

    /**
     * Get Date Format
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getUIDateFormat = function () {
        return getConstant('WD_UI_JS_DATE_FORMAT');
    };

    /**
     * Get Deliverable Countries
     *
     * @since 1.4.0
     * @returns {String}
     */
    helper.getDeliverableCountries = function () {
        return getConstant('WD_DELIVERABLE_COUNTRIES');
    };

    /**
     * Get Job Types Filter
     *
     * @since 1.6.0
     * @returns {String}
     */
    helper.getJobTypesFilter = function () {
        return getConstant('WD_ORDERING_UI_JOB_TYPES_FILTER');
    };

    /**
     * Get Joint Switching Mode
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getJointSwitchingMode = function () {
        return getConstant('WD_JOINT_SWITCHING_MODE');
    };

    /**
     * Get Locale Currency Symbol
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getLocaleCurrencySymbol = function () {
        return getConstant('WD_LOCALE_CURRENCY_SYMBOL');
    };

    /**
     * Get Locale Currency Unit
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getLocaleCurrencyUnit = function () {
        return getConstant('WD_LOCALE_CURRENCY_UNIT');
    };

    /**
     * Get Locale Language
     *
     * @since 1.4.0
     * @returns {String}
     */
    helper.getLocaleLanguage = function () {
        return getConstant('WD_LOCALE_LANG');
    };

    /**
     * Get Locale Weight Symbol
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getLocaleWeightSymbol = function () {
        return getConstant('WD_LOCALE_WEIGHT_SYMBOL');
    };

    /**
     * Get Locale Weight Unit
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getLocaleWeightUnit = function () {
        return getConstant('WD_LOCALE_WEIGHT_SYMBOL');
    };

    /**
     * Get Plumbing Override
     * Try to fetch a constant that is a URL to override the Piping & Plumbing link in the header menu & Dashboard welcome page.
     * NOTE: this is a temporary workaround until a more robust system to allow custom URL overrides is implemented.
     * @returns {String}
     */
    helper.getPlumbingUrlOverride = function() {
        return getConstant('WD_PLUMBING_URL_OVERRIDE');
    };

    /**
     * Get Terms and Conditions
     *
     * @since 1.0.0
     * @returns {String}
     */
    helper.getTermsAndConditions = function () {
        return getConstant('WD_TERMS_AND_CONDITIONS');
    };

    /**
     * Is 3D Model Metric to Imperial Conversion enabled
     * - If set to 'imperial', then all 3D model renderings will automatically convert values from metric to imperial.
     *
     * @returns {Boolean}
     */
    helper.is3DModelImperialConversionEnabled = function () {
        return getConstant('WD_3D_MODEL_IMPERIAL_CONVERSION') === 'enabled';
    };

    /**
     * Is Timecard Cloning for Crews Enabled
     *
     * @since 2.56.0
     * @returns {Boolean}
     */
    helper.isCloneTimecardsForCrewEnabled = function () {
        return getConstant('WD_ENABLE_CLONE_TIMECARDS_FOR_CREW') === 'enabled';
    };

    /**
     * Is custom order form fields enabled
     * - If set to 1, the custom order form fields should be enabled for the order submission page and the order details page
     *
     * @returns {Boolean}
     */
    helper.isCustomOrderFormFieldsEnabled = function () {
        return getConstant('WD_ENABLE_CUSTOM_ORDER_FORM_FIELDS') === '1';
    };

    /**
     * Is custom timecards form fields enabled
     * - If set to 1, then custom fields will be displayed for timecard entry input.
     *
     * @returns {Boolean}
     */
    helper.isCustomTimecardFormFieldsEnabled = function () {
        return getConstant('WD_ENABLE_CUSTOM_TIMECARDS_FORM_FIELDS') === '1';
    };

    /**
     * Is Conversion Enabled
     *
     * @since 1.8.0
     * @returns {Boolean}
     */
    helper.isImperialConversionEnabled = function () {
        return getConstant('WD_IMPERIAL_CONVERSION') === 'enabled' && helper.isMetric();
    };

    /**
     * Is Metric Deployment
     *
     * @since 1.16.0
     * @returns {Boolean}
     */
    helper.isMetric = function () {
        return getConstant('WD_MEASUREMENT_SYSTEM') === 'metric';
    };

    /**
     * Is Modern Field Timecard UI
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isModernFieldTimecardUI = function () {
        return getConstant('WD_ROUTING_FIELD_TIMECARD') === 'modern';
    };

    /**
     * Is Modern PDF Export
     *
     * @since 2.4.0
     * @returns {Boolean}
     */
    helper.isModernPdfExport = function () {
        return getConstant('WD_ROUTING_PDF_EXPORTS_API') === 'modern';
    };

    /**
     * Is The PO Number Field Required
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isPONumberRequired = function () {
        return getConstant('WD_ORDERING_UI_PO_NUMBER_REQUIRED') === 'enabled';
    };

    /**
     * Is Productivity Report Enabled
     *
     * @since 2.47.0
     * @returns {Boolean}
     */
    helper.isProductivityReportEnabled = function () {
        return getConstant('WD_ENABLE_PRODUCTIVITY_REPORT') === 'enabled';
    };

    /**
     * Is Order Taxes Included
     * - If true then the cart total text will indicate that the order price includes taxes
     *
     * @returns {Boolean}
     */
    helper.isOrderTaxesIncluded = function () {
        return getConstant('WD_ORDER_TOTALS_INCLUDE_TAX') === 'true';
    };

    /**
     * Is Ordering UI v2 enabled
     * - When enabled the URLs for the submit order and the order confirmation page links direct to the v2 (angular) version UI
     *
     * @since 3.13.0
     * @returns {Boolean}
     */
    helper.isOrderingUIv2Enabled = function () {
        return getConstant('WD_ENABLE_ORDERING_UI_V2') === '1';
    };

    /**
     * Is The Tag Field Required
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isTagRequired = function () {
        return getConstant('WD_ORDERING_UI_TAG_REQUIRED') === 'enabled';
    };

    /****************************************** Initialization ********************************************************/

    /**
     * Set Constants
     *
     * @since 1.0.0
     * @param {Object} input
     */
    helper.setConstants = function (input) {

        // Set Constants by Name
        for(var i = 0; i < input.length; i++) {
            var constant = input[i];
            constants[constant.name] = constant.value;
        }

    };

    /***************************************** Private Functions ******************************************************/

    /**
     * Get Constant Value by Key
     *
     * @since 1.0.0
     * @param {String} key
     * @returns {String}
     */
    function getConstant(key) {
        return constants[key]
    }

    // Return
    return helper;

};
globalThis.WebductConstants = WebductConstants;
// EOF